import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Loader2, Check, XCircle, Undo2, Pencil, Sparkles, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { ArtThumb } from "@/components/riptag/TrackTable";

const FILTERS = [["needs_id", "Needs ID"], ["suggested", "Suggestions"], ["applied", "Applied"], ["all", "All issues"]];
const STATE_CHIP = {
  needs_id: ["Needs ID", "bg-amber-500/15 text-amber-300 border-amber-500/40"],
  suggested: ["Suggestion ready", "bg-cyan-500/15 text-cyan-300 border-cyan-500/40"],
  applied: ["Applied", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"],
  rejected: ["Rejected", "bg-slate-500/15 text-slate-300 border-slate-500/40"],
  unidentified: ["No match found", "bg-rose-500/15 text-rose-300 border-rose-500/40"],
};

export const AutoTagPanel = ({ onOpenLibrary, onChanged }) => {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("needs_id");
  const [sel, setSel] = useState(new Set());
  const [useAi, setUseAi] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async (f = filter) => {
    try {
      const [o, q] = await Promise.all([
        api.get("/music/autotag/overview"),
        api.get("/music/autotag/queue", { params: { filter: f, page_size: 100 } }),
      ]);
      setOv(o.data);
      setRows(q.data.rows);
    } catch { toast.error("Couldn't load the auto-tagging queue"); setRows([]); }
  }, [filter]);
  useEffect(() => { load(filter); setSel(new Set()); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearInterval(pollRef.current), []);
  const watchJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/rip/identify/jobs/${jobId}`);
        setJob(r.data);
        if (r.data.status === "done") {
          clearInterval(pollRef.current);
          const s = r.data.summary || {};
          toast.success(`Identification finished — ${s.suggested || 0} suggestion${(s.suggested || 0) === 1 ? "" : "s"}, ${s.unidentified || 0} without a match`);
          setJob(null);
          load();
          onChanged?.();
        }
      } catch { clearInterval(pollRef.current); setJob(null); }
    }, 1500);
  };

  const identifySelected = async () => {
    setBusy("bulk");
    try {
      const r = await api.post("/music/identify", { track_ids: [...sel], use_ai_fallback: useAi });
      setJob({ job_id: r.data.job_id, progress: 0, total: r.data.total, status: "running" });
      watchJob(r.data.job_id);
      setSel(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start identification"); }
    setBusy("");
  };

  const identifyOne = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/music/identify/${id}`, { use_ai_fallback: useAi });
      setRows((p) => p.map((t) => (t.track_id === id ? r.data.track : t)));
      setOpen(id);
      api.get("/music/autotag/overview").then((o) => setOv(o.data)).catch(() => {});
      toast[r.data.suggestions?.length ? "success" : "info"](
        r.data.suggestions?.length ? `${r.data.suggestions.length} match${r.data.suggestions.length === 1 ? "" : "es"} found — review below` : "No reliable match found for this track");
    } catch (e) { toast.error(e?.response?.data?.detail || "Identify failed"); }
    setBusy("");
  };

  const act = async (id, fn, ok) => {
    setBusy(id);
    try {
      const r = await fn();
      if (r?.data?.track) setRows((p) => p.map((t) => (t.track_id === id ? r.data.track : t)));
      else load();
      if (ok) toast.success(ok);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allIds = (rows || []).map((t) => t.track_id);
  const allSel = allIds.length > 0 && allIds.every((i) => sel.has(i));

  if (rows === null) return <Loader2 size={16} className="animate-spin text-ink-muted" />;

  return (
    <div data-testid="ml-autotag" className="space-y-3">
      <p className="type-body text-ink-muted">
        Audio fingerprinting (AcoustID + MusicBrainz) names your untitled tracks — the same engine as Rip &amp; Tag.
        In the library nothing is ever applied automatically: review each match, then Apply, Edit or Reject. Undo anytime.
      </p>

      {ov && (
        <div className="flex flex-wrap gap-2" data-testid="ml-autotag-stats">
            {[["needs", "need identification", "text-amber-300"], ["suggested", "suggestions waiting", "text-cyan-300"],
            ["applied", "applied", "text-emerald-300"], ["missing_year", "missing a year", "text-ink-secondary"]].map(([k, l, c]) => (
            <span key={k} className="rounded-full border border-line bg-bg-surface px-3 py-1 text-xs">
              <b className={c}>{k === "needs" ? (ov.needs_id || 0) + (ov.unidentified || 0) : ov[k]}</b> <span className="text-ink-muted">{l}</span>
            </span>
          ))}
          <span className="rounded-full border border-line bg-bg-surface px-3 py-1 text-xs text-ink-muted">
            Fingerprinting: <b className={ov.acoustid_configured ? "text-emerald-300" : "text-rose-300"}>{ov.acoustid_configured ? "ready (free)" : "not configured"}</b>
            {" · "}AI lyrics fallback: <b className="text-ink-secondary">{ov.ai_cost} credit/track</b>
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-line bg-bg-surface p-1">
          {FILTERS.map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)} data-testid={`ml-autotag-filter-${id}`}
              className={`rounded-md px-2.5 py-1 text-[11px] font-bold transition ${filter === id ? "bg-brand-amber text-bg-base" : "text-ink-muted hover:text-ink-primary"}`}>
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-ink-secondary cursor-pointer">
          <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)}
            data-testid="ml-autotag-ai-toggle" className="accent-amber-500" />
          AI lyrics fallback when the fingerprint finds nothing ({ov?.ai_cost ?? 1} credit/track used)
        </label>
        <div className="flex-1" />
        <button onClick={identifySelected} disabled={!sel.size || !!busy || !!job} data-testid="ml-autotag-identify-selected"
          className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-50">
          {busy === "bulk" ? <Loader2 size={13} className="animate-spin" /> : <Fingerprint size={13} />}
          Identify selected ({sel.size})
        </button>
        <button onClick={() => load()} className="rounded-md border border-line p-2 text-ink-muted hover:text-ink-primary" data-testid="ml-autotag-refresh">
          <RefreshCw size={13} />
        </button>
      </div>

      {job && (
        <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-3 py-2 text-xs" data-testid="ml-autotag-job">
          <div className="flex items-center gap-2 text-cyan-300 font-mono">
            <Loader2 size={12} className="animate-spin" /> Identifying {job.progress}/{job.total}…
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-bg-elevated overflow-hidden">
            <div className="h-full bg-cyan-400 transition-all" style={{ width: `${(job.progress / Math.max(1, job.total)) * 100}%` }} />
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-8 text-center type-body text-ink-muted" data-testid="ml-autotag-empty">
          <Check size={20} className="mx-auto mb-2 text-emerald-400" />
          Nothing in this view — {filter === "needs_id" ? "every library track has a title and artist." : "no tracks match this filter."}
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line" data-testid="ml-autotag-list">
          <div className="flex items-center gap-3 px-3 py-2">
            <input type="checkbox" checked={allSel} data-testid="ml-autotag-select-all"
              onChange={() => setSel(allSel ? new Set() : new Set(allIds))} className="accent-amber-500" />
            <span className="type-card-meta-label text-ink-muted">{rows.length} track{rows.length === 1 ? "" : "s"}</span>
          </div>
          {rows.map((t) => {
            const [chipLabel, chipCls] = STATE_CHIP[t.tag_state] || STATE_CHIP.needs_id;
            const expanded = open === t.track_id;
            return (
              <div key={t.track_id} data-testid={`ml-autotag-row-${t.track_id}`}>
                <div className="flex items-center gap-3 px-3 py-2">
                  <input type="checkbox" checked={sel.has(t.track_id)} onChange={() => toggle(t.track_id)}
                    data-testid={`ml-autotag-check-${t.track_id}`} className="accent-amber-500" />
                  <ArtThumb url={t.art_url} size="w-9 h-9" />
                  <button onClick={() => setOpen(expanded ? null : t.track_id)} className="min-w-0 flex-1 text-left">
                    <div className="type-list-title text-ink-primary truncate">{t.title || t.filename || "Untitled"}</div>
                    <div className="type-list-subtitle text-ink-muted truncate">
                      {t.artist || "Unknown artist"} · {t.album || "—"} · {Math.round(t.duration || 0)}s
                    </div>
                  </button>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${chipCls}`}>{chipLabel}</span>
                  {t.tag_state === "applied" ? (
                    <button onClick={() => act(t.track_id, () => api.post(`/music/tracks/${t.track_id}/restore`), "Original metadata restored")}
                      disabled={!!busy} data-testid={`ml-autotag-undo-${t.track_id}`}
                      className="shrink-0 flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[10px] font-bold text-ink-secondary hover:text-ink-primary disabled:opacity-50">
                      <Undo2 size={11} /> Undo
                    </button>
                  ) : (
                    <button onClick={() => identifyOne(t.track_id)} disabled={!!busy || !!job}
                      data-testid={`ml-autotag-identify-${t.track_id}`}
                      className="shrink-0 flex items-center gap-1 rounded-md border border-amber-500/50 px-2 py-1 text-[10px] font-bold text-brand-amber hover:bg-amber-500/10 disabled:opacity-50">
                      {busy === t.track_id ? <Loader2 size={11} className="animate-spin" /> : <Fingerprint size={11} />} Identify
                    </button>
                  )}
                </div>
                {expanded && (t.identify_suggestions || []).length > 0 && (
                  <div className="px-3 pb-3 space-y-2" data-testid={`ml-autotag-suggs-${t.track_id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      <div className="rounded-lg border border-line bg-bg-base p-2.5">
                        <div className="type-card-meta-label text-ink-muted mb-1">Current metadata</div>
                        <div className="text-ink-primary font-semibold truncate">{t.title || "(untitled)"}</div>
                        <div className="text-ink-muted truncate">{t.artist || "Unknown artist"} · {t.album || "no album"}{t.year ? ` · ${t.year}` : ""}</div>
                      </div>
                      {t.identify_suggestions.map((s, i) => (
                        <div key={i} className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-2.5" data-testid={`ml-autotag-sugg-${t.track_id}-${i}`}>
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="rounded-full bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 text-[9px] font-mono font-bold">
                              {s.source === "acoustid" ? `AcoustID ${s.score_pct}% · ${s.quality}` : "AI lyrics suggestion"}
                            </span>
                            {s.variant && <span className="rounded-full border border-line px-1.5 py-0.5 text-[9px] font-mono uppercase text-ink-secondary">{s.variant.replace("_", " ")}</span>}
                            {s.duration_delta != null && <span className="text-[9px] text-ink-muted font-mono">Δ{s.duration_delta}s</span>}
                          </div>
                          <div className="text-ink-primary font-semibold truncate">{s.title}</div>
                          <div className="text-ink-muted truncate">{s.artists}{s.album ? ` · ${s.album}` : ""}</div>
                          {s.basis && <div className="text-[10px] text-ink-muted italic mt-0.5">{s.basis}</div>}
                          <div className="flex gap-1.5 mt-2">
                            <button onClick={() => act(t.track_id, () => api.post(`/music/identify/${t.track_id}/apply`, { index: i }), "Match applied — original kept for undo")}
                              disabled={!!busy} data-testid={`ml-autotag-apply-${t.track_id}-${i}`}
                              className="flex items-center gap-1 rounded-full bg-emerald-500/90 text-black px-2.5 py-1 text-[10px] font-bold hover:opacity-90 disabled:opacity-50">
                              <Check size={10} /> Apply
                            </button>
                            <button onClick={() => onOpenLibrary?.({ q: t.title || t.filename })}
                              data-testid={`ml-autotag-edit-${t.track_id}`}
                              className="flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[10px] font-bold text-ink-secondary hover:text-ink-primary">
                              <Pencil size={10} /> Edit
                            </button>
                            <button onClick={() => act(t.track_id, () => api.post(`/music/identify/${t.track_id}/reject`), "Suggestions rejected")}
                              disabled={!!busy} data-testid={`ml-autotag-reject-${t.track_id}`}
                              className="flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[10px] font-bold text-ink-muted hover:text-rose-300 disabled:opacity-50">
                              <XCircle size={10} /> Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expanded && !(t.identify_suggestions || []).length && (
                  <div className="px-3 pb-3 text-[11px] text-ink-muted flex items-center gap-1.5">
                    <Sparkles size={11} /> No suggestions yet — click Identify to fingerprint this track.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
